// src/Onboarding.jsx
// Full 5-step onboarding flow
// Step 1: 8-question questionnaire (QuestionnaireStep)
// Step 2: Connect booking system
// Step 3: Review platform URLs
// Step 4: AI template picker (TemplatePickerStep)
// Step 5: Go live

import { useState } from "react";
import QuestionnaireStep from "./QuestionnaireStep";
import TemplatePickerStep from "./TemplatePickerStep";

const BACKEND = "https://melvo-backend-production.up.railway.app";

const T = {
  bg:       "#060608",
  surface:  "#0f0f16",
  surface2: "#181825",
  border:   "#252535",
  accent:   "#00e5a0",
  text:     "#f2f2fa",
  muted:    "#7a7a96",
  warn:     "#ff8c42",
};

const INTEGRATIONS = [
  { id: "square",   name: "Square",       emoji: "⬛", desc: "Appointments, POS & retail",    status: "live" },
  { id: "booksy",   name: "Booksy",       emoji: "📅", desc: "Salons, barbers & beauty",       status: "soon" },
  { id: "toast",    name: "Toast",        emoji: "🍞", desc: "Restaurant POS",                 status: "soon" },
  { id: "clover",   name: "Clover",       emoji: "🍀", desc: "Retail & restaurant POS",        status: "soon" },
  { id: "resy",     name: "Resy",         emoji: "🍷", desc: "Restaurant reservations",        status: "soon" },
  { id: "aloha",    name: "Aloha (NCR)",  emoji: "🌺", desc: "Restaurant enterprise POS",      status: "soon" },
  { id: "tabbit",   name: "Tabbit",       emoji: "🐇", desc: "Restaurant management",          status: "soon" },
  { id: "vagaro",   name: "Vagaro",       emoji: "💆", desc: "Salons & wellness",               status: "soon" },
  { id: "mindbody", name: "Mindbody",     emoji: "🧘", desc: "Gyms & wellness studios",        status: "soon" },
  { id: "csv",      name: "CSV / Manual", emoji: "📋", desc: "Upload existing customers",      status: "live" },
];

const PLATFORMS = [
  { id: "google",      name: "Google",      emoji: "⭐", fact: "Most impactful",         placeholder: "https://g.page/r/YOUR-ID/review" },
  { id: "yelp",        name: "Yelp",        emoji: "🔴", fact: "40M+ monthly visitors",   placeholder: "https://www.yelp.com/writeareview/biz/YOUR-ID" },
  { id: "facebook",    name: "Facebook",    emoji: "💙", fact: "Trusted by locals",       placeholder: "https://www.facebook.com/YOUR-PAGE/reviews" },
  { id: "tripadvisor", name: "TripAdvisor", emoji: "🦉", fact: "Best for restaurants",    placeholder: "https://www.tripadvisor.com/YOUR-PAGE" },
  { id: "bbb",         name: "BBB",         emoji: "🔵", fact: "Trust signal",            placeholder: "https://www.bbb.org/us/YOUR-PAGE" },
  { id: "angi",        name: "Angi",        emoji: "🟠", fact: "Best for home services",  placeholder: "https://www.angi.com/companylist/YOUR-ID" },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .ob-root {
    min-height: 100vh; background: ${T.bg}; color: ${T.text};
    font-family: 'DM Sans', sans-serif;
    display: flex; flex-direction: column; align-items: center;
    padding: 32px 24px 80px; position: relative; overflow-x: hidden;
  }
  .ob-grid {
    position: fixed; inset: 0; pointer-events: none;
    background-image: linear-gradient(rgba(0,229,160,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,160,0.025) 1px, transparent 1px);
    background-size: 48px 48px;
  }
  .ob-glow {
    position: fixed; top: -300px; left: 50%; transform: translateX(-50%);
    width: 800px; height: 800px; border-radius: 50%;
    background: radial-gradient(ellipse, rgba(0,229,160,0.05) 0%, transparent 65%);
    pointer-events: none;
  }
  .ob-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 900;
    color: ${T.accent}; letter-spacing: -0.5px; margin-bottom: 32px; position: relative; z-index: 1;
  }

  /* Tracker */
  .ob-tracker { display: flex; align-items: center; margin-bottom: 40px; position: relative; z-index: 1; }
  .ob-track-step { display: flex; flex-direction: column; align-items: center; gap: 6px; }
  .ob-track-dot {
    width: 30px; height: 30px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; transition: all 0.35s ease;
    border: 1.5px solid ${T.border}; color: ${T.muted}; background: ${T.surface}; z-index: 1; position: relative;
  }
  .ob-track-dot.active { border-color: ${T.accent}; color: ${T.accent}; background: rgba(0,229,160,0.08); box-shadow: 0 0 0 4px rgba(0,229,160,0.07); }
  .ob-track-dot.done { border-color: ${T.accent}; background: ${T.accent}; color: #000; }
  .ob-track-label { font-size: 10px; font-weight: 600; color: ${T.muted}; text-transform: uppercase; letter-spacing: 0.8px; white-space: nowrap; }
  .ob-track-label.active { color: ${T.accent}; }
  .ob-track-line { width: 44px; height: 1.5px; background: ${T.border}; margin-bottom: 18px; transition: background 0.35s; flex-shrink: 0; }
  .ob-track-line.done { background: ${T.accent}; }

  /* Card */
  .ob-card {
    background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 20px; padding: 48px;
    width: 100%; max-width: 640px; position: relative; z-index: 1;
    animation: obUp 0.3s ease;
  }
  .ob-card.wide { max-width: 760px; }
  @keyframes obUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }

  /* Step header */
  .ob-step-tag {
    display: inline-flex; align-items: center; gap: 7px; padding: 5px 11px; border-radius: 20px;
    background: rgba(0,229,160,0.08); border: 1px solid rgba(0,229,160,0.2); margin-bottom: 14px;
  }
  .ob-step-num { color: ${T.accent}; font-size: 10px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
  .ob-step-div { width: 1px; height: 9px; background: rgba(0,229,160,0.2); }
  .ob-step-name { color: ${T.accent}; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
  .ob-title { font-family: 'Syne', sans-serif; font-size: clamp(20px, 3.5vw, 26px); font-weight: 800; letter-spacing: -0.5px; line-height: 1.25; margin-bottom: 8px; }
  .ob-title em { font-style: normal; color: ${T.accent}; }
  .ob-sub { font-size: 14px; color: ${T.muted}; line-height: 1.65; margin-bottom: 28px; }

  /* Integration grid */
  .ob-int-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .ob-int-card {
    background: ${T.surface2}; border: 1.5px solid ${T.border}; border-radius: 12px; padding: 15px;
    cursor: pointer; transition: all 0.18s; display: flex; align-items: center; gap: 11px;
    position: relative; text-align: left; width: 100%;
  }
  .ob-int-card:hover:not(.soon) { border-color: rgba(0,229,160,0.3); transform: translateY(-1px); }
  .ob-int-card.selected { border-color: ${T.accent}; background: rgba(0,229,160,0.06); }
  .ob-int-card.soon { opacity: 0.45; cursor: not-allowed; }
  .ob-int-icon { font-size: 22px; flex-shrink: 0; }
  .ob-int-name { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
  .ob-int-desc { font-size: 11px; color: ${T.muted}; }
  .ob-int-badge { position: absolute; top: 7px; right: 7px; font-size: 9px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; padding: 2px 6px; border-radius: 20px; }
  .ob-int-badge.live { background: rgba(0,229,160,0.12); color: ${T.accent}; }
  .ob-int-badge.soon { background: rgba(255,140,66,0.1); color: ${T.warn}; }
  .ob-int-check { margin-left: auto; width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid ${T.border}; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; transition: all 0.18s; }
  .ob-int-card.selected .ob-int-check { background: ${T.accent}; border-color: ${T.accent}; color: #000; }

  /* Platforms */
  .ob-plat-row {
    display: flex; align-items: center; padding: 13px 15px; border-radius: 12px;
    background: ${T.surface2}; border: 1.5px solid ${T.border}; margin-bottom: 10px;
    gap: 12px; transition: border-color 0.2s;
  }
  .ob-plat-row.on { border-color: rgba(0,229,160,0.25); }
  .ob-plat-emoji { font-size: 20px; flex-shrink: 0; }
  .ob-plat-name { font-size: 13px; font-weight: 700; }
  .ob-plat-fact { font-size: 11px; color: ${T.muted}; }
  .ob-plat-url {
    flex: 1; background: transparent; border: none; border-bottom: 1px solid ${T.border};
    color: ${T.text}; font-size: 12px; font-family: 'DM Sans', sans-serif;
    padding: 2px 4px; outline: none; transition: border-color 0.2s; min-width: 0;
  }
  .ob-plat-url:focus { border-bottom-color: ${T.accent}; }
  .ob-plat-url::placeholder { color: ${T.muted}; }
  .ob-toggle {
    width: 40px; height: 22px; border-radius: 11px; background: ${T.border};
    border: none; cursor: pointer; position: relative; transition: background 0.2s; flex-shrink: 0; outline: none;
  }
  .ob-toggle.on { background: ${T.accent}; }
  .ob-toggle::after { content:''; position:absolute; width:16px; height:16px; border-radius:50%; background:white; top:3px; left:3px; transition:transform 0.2s; }
  .ob-toggle.on::after { transform: translateX(18px); }

  /* Note */
  .ob-note {
    padding: 12px 14px; background: rgba(255,255,255,0.02); border: 1px solid ${T.border};
    border-radius: 10px; font-size: 12px; color: ${T.muted}; line-height: 1.6;
    display: flex; gap: 9px; align-items: flex-start;
  }
  .ob-note.accent { background: rgba(0,229,160,0.04); border-color: rgba(0,229,160,0.15); }

  /* Done */
  .ob-done { text-align: center; padding: 8px 0; }
  .ob-done-icon {
    width: 68px; height: 68px; border-radius: 50%; background: rgba(0,229,160,0.1);
    border: 2px solid ${T.accent}; display: flex; align-items: center; justify-content: center;
    font-size: 28px; margin: 0 auto 22px;
    animation: obPop 0.5s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes obPop { from { transform:scale(0); opacity:0; } to { transform:scale(1); opacity:1; } }
  .ob-done-title { font-family: 'Syne', sans-serif; font-size: 30px; font-weight: 900; letter-spacing: -1px; margin-bottom: 10px; }
  .ob-done-title em { color: ${T.accent}; font-style: normal; }
  .ob-done-sub { font-size: 15px; color: ${T.muted}; line-height: 1.7; max-width: 400px; margin: 0 auto 24px; }
  .ob-done-rows { display: flex; flex-direction: column; gap: 8px; margin-bottom: 24px; text-align: left; }
  .ob-done-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 10px; background: ${T.surface2}; border: 1px solid ${T.border}; font-size: 13px; }

  /* Actions */
  .ob-actions { display: flex; gap: 10px; margin-top: 28px; }
  .ob-btn { flex: 1; padding: 13px 20px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; border: none; font-family: 'DM Sans', sans-serif; }
  .ob-btn-primary { background: ${T.accent}; color: #000; }
  .ob-btn-primary:hover:not(:disabled) { background: #00ffb3; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,229,160,0.28); }
  .ob-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; }
  .ob-btn-ghost { background: transparent; color: ${T.muted}; border: 1.5px solid ${T.border}; }
  .ob-btn-ghost:hover { border-color: ${T.muted}; color: ${T.text}; }
  .ob-skip { text-align: center; margin-top: 14px; font-size: 12px; color: ${T.muted}; cursor: pointer; transition: color 0.2s; }
  .ob-skip:hover { color: ${T.text}; }

  @media (max-width: 600px) {
    .ob-card { padding: 28px 20px; }
    .ob-int-grid { grid-template-columns: 1fr; }
    .ob-track-line { width: 24px; }
    .ob-track-label { display: none; }
  }
`;

// ── Step 2 ────────────────────────────────────────────────────
function ConnectStep({ data, onChange }) {
  const sel = data.integration;
  return (
    <>
      <div className="ob-step-tag"><span className="ob-step-num">02</span><span className="ob-step-div"/><span className="ob-step-name">Connect</span></div>
      <h2 className="ob-title">Connect your <em>booking system.</em></h2>
      <p className="ob-sub">We pull customer names, phone numbers, and visit dates automatically — zero manual entry.</p>
      <div className="ob-int-grid">
        {INTEGRATIONS.map(int => (
          <button
            key={int.id} type="button"
            className={`ob-int-card${sel === int.id ? " selected" : ""}${int.status === "soon" ? " soon" : ""}`}
            onClick={() => { if (int.status !== "soon") onChange({ integration: int.id }); }}
          >
            <span className="ob-int-icon">{int.emoji}</span>
            <div><div className="ob-int-name">{int.name}</div><div className="ob-int-desc">{int.desc}</div></div>
            <span className={`ob-int-badge ${int.status}`}>{int.status === "live" ? "Live" : "Soon"}</span>
            {int.status === "live" && <div className="ob-int-check">{sel === int.id ? "✓" : ""}</div>}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        {sel === "square" && <div className="ob-note accent"><span>🔗</span><div><strong style={{color:T.text}}>Connecting Square</strong><br/>You'll authorize Melvo with read-only access to customers and appointments.</div></div>}
        {sel === "csv"    && <div className="ob-note"><span>📋</span><div>Upload a CSV: <strong style={{color:T.text}}>first_name, last_name, phone, email, visit_date</strong></div></div>}
        {!sel             && <div className="ob-note"><span>💡</span><div>Not on any of these? Select <strong style={{color:T.text}}>CSV / Manual</strong> to get started today.</div></div>}
      </div>
    </>
  );
}

// ── Step 3 ────────────────────────────────────────────────────
function PlatformsStep({ data, onChange }) {
  const isOn = (id) => data.platforms?.[id] !== false && (data.platforms?.[id] === true || id === "google");
  const toggle = (id) => onChange({ platforms: { ...(data.platforms || { google: true }), [id]: !isOn(id) } });
  const setUrl = (id, val) => onChange({ platformUrls: { ...(data.platformUrls || {}), [id]: val } });

  return (
    <>
      <div className="ob-step-tag"><span className="ob-step-num">03</span><span className="ob-step-div"/><span className="ob-step-name">Platforms</span></div>
      <h2 className="ob-title">Where should reviews <em>go?</em></h2>
      <p className="ob-sub">Toggle your platforms and paste your review links. We send customers exactly where you want them.</p>
      {PLATFORMS.map(p => {
        const on = isOn(p.id);
        return (
          <div key={p.id} className={`ob-plat-row${on ? " on" : ""}`}>
            <span className="ob-plat-emoji">{p.emoji}</span>
            <div style={{ width: 88, flexShrink: 0 }}>
              <div className="ob-plat-name">{p.name}</div>
              <div className="ob-plat-fact">{p.fact}</div>
            </div>
            {on && <input className="ob-plat-url" placeholder={p.placeholder} value={data.platformUrls?.[p.id] || ""} onChange={e => setUrl(p.id, e.target.value)} />}
            <button className={`ob-toggle${on ? " on" : ""}`} onClick={() => toggle(p.id)} />
          </div>
        );
      })}
      <div className="ob-note" style={{ marginTop: 8 }}>
        <span>💡</span><div>Find your Google link: search your business on Maps → click "Write a review" → copy the URL.</div>
      </div>
    </>
  );
}

// ── Step 5 ────────────────────────────────────────────────────
function GoLiveStep({ data }) {
  const active = PLATFORMS.filter(p => data.platforms?.[p.id] !== false && (data.platforms?.[p.id] === true || p.id === "google"));
  const int = INTEGRATIONS.find(i => i.id === data.integration);
  const biz = (data.business_info || "").split(/[—\-–]/)[0].trim() || "Your Business";
  return (
    <div className="ob-done">
      <div className="ob-done-icon">✓</div>
      <div className="ob-done-title">You're <em>live.</em></div>
      <p className="ob-done-sub">Your first review request fires 2 hours after a customer's next visit. Melvo handles everything from here.</p>
      <div className="ob-done-rows">
        <div className="ob-done-row"><span>🏢</span><strong>{biz}</strong></div>
        <div className="ob-done-row"><span>🔗</span><span>{int ? `${int.emoji} ${int.name} connected` : "Manual / CSV — upgrade anytime"}</span></div>
        <div className="ob-done-row"><span>⭐</span><span>{active.map(p => p.name).join(", ")}</span></div>
        <div className="ob-done-row"><span>✦</span><span>{data.selectedTemplates?.length || 0} custom templates rotating on every send</span></div>
        <div className="ob-done-row"><span>⏰</span><span>SMS 2 hrs post-visit · Email follow-up day 4</span></div>
        <div className="ob-done-row"><span>💬</span><span>150 messages in your trial · 14 days free</span></div>
      </div>
    </div>
  );
}

// ── Save ──────────────────────────────────────────────────────
// ── State → IANA timezone map ─────────────────────────────────────────────
const STATE_TIMEZONE = {
  AL:"America/Chicago",  AK:"America/Anchorage", AZ:"America/Phoenix",
  AR:"America/Chicago",  CA:"America/Los_Angeles",CO:"America/Denver",
  CT:"America/New_York", DE:"America/New_York",  FL:"America/New_York",
  GA:"America/New_York", HI:"Pacific/Honolulu",  ID:"America/Denver",
  IL:"America/Chicago",  IN:"America/Indiana/Indianapolis", IA:"America/Chicago",
  KS:"America/Chicago",  KY:"America/New_York",  LA:"America/Chicago",
  ME:"America/New_York", MD:"America/New_York",  MA:"America/New_York",
  MI:"America/Detroit",  MN:"America/Chicago",   MS:"America/Chicago",
  MO:"America/Chicago",  MT:"America/Denver",    NE:"America/Chicago",
  NV:"America/Los_Angeles",NH:"America/New_York",NJ:"America/New_York",
  NM:"America/Denver",   NY:"America/New_York",  NC:"America/New_York",
  ND:"America/Chicago",  OH:"America/New_York",  OK:"America/Chicago",
  OR:"America/Los_Angeles",PA:"America/New_York",RI:"America/New_York",
  SC:"America/New_York", SD:"America/Chicago",   TN:"America/Chicago",
  TX:"America/Chicago",  UT:"America/Denver",    VT:"America/New_York",
  VA:"America/New_York", WA:"America/Los_Angeles",WV:"America/New_York",
  WI:"America/Chicago",  WY:"America/Denver",    DC:"America/New_York",
};

function timezoneFromCityState(cityState) {
  if (!cityState) return null;
  const match = cityState.trim().match(/,\s*([A-Za-z]{2})\s*$/);
  if (!match) return null;
  const abbr = match[1].toUpperCase();
  return STATE_TIMEZONE[abbr] || null;
}

async function saveOnboarding(data, token) {
  const h = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  // Derive primary_platform — first active platform the client toggled on, fallback to google
  const activePlatforms = PLATFORMS.filter(
    p => data.platforms?.[p.id] !== false && (data.platforms?.[p.id] === true || p.id === "google")
  );
  const primaryPlatform = activePlatforms[0]?.id || "google";

  await fetch(`${BACKEND}/businesses/me`, {
    method: "PUT", headers: h,
    body: JSON.stringify({
      name:                (data.business_info || "").split(/[—\-–]/)[0].trim(),
      tone:                data.tone,
      years_in_business:   data.years_in_business,
      differentiator:      data.differentiator,
      customer_base:       data.customer_base,
      staff_known:         data.staff_known,
      avoid_phrases:       data.avoid_phrases,
      brand_feeling:       data.brand_feeling,
      primary_platform:    primaryPlatform,
      timezone:            timezoneFromCityState(data.city_state),
      preferred_send_hour: data.preferred_send_hour != null ? parseInt(data.preferred_send_hour) : 18,
      onboarding_complete: true,
    }),
  });

  const platforms = activePlatforms
    .map(p => ({ platform: p.id, review_url: data.platformUrls?.[p.id] || "" }));

  await fetch(`${BACKEND}/businesses/me/platforms`, {
    method: "PUT", headers: h,
    body: JSON.stringify({ platforms }),
  });

  if (data.templates?.length) {
    const rows = data.templates.map((text, i) => ({
      template_text:   text,
      is_active:       (data.selectedTemplates || []).includes(i),
      is_ai_generated: true,
    }));
    await fetch(`${BACKEND}/businesses/me/templates`, {
      method: "POST", headers: h,
      body: JSON.stringify({ templates: rows }),
    });
  }
}

// ── Step config ───────────────────────────────────────────────
const STEPS = [
  { label: "Profile" },
  { label: "Connect" },
  { label: "Platforms" },
  { label: "Templates" },
  { label: "Go Live" },
];

// ── Main ──────────────────────────────────────────────────────
export default function Onboarding({ token, onComplete }) {
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [data, setData]     = useState({
    business_info: "", business_type: "", city_state: "", typical_visit: "",
    years_in_business: "", tone: "", differentiator: "",
    customer_base: "", customer_feeling: "", avoid_phrases: "", brand_feeling: "",
    integration: null,
    platforms: { google: true }, platformUrls: {},
    templates: null, selectedTemplates: [],
  });

  const update = (patch) => setData(d => ({ ...d, ...patch }));

  const canNext = () => {
    if (step === 2) return !!data.integration;
    if (step === 4) return (data.selectedTemplates?.length || 0) >= 3;
    return true;
  };

  const next = async () => {
    if (step < 5) { setStep(s => s + 1); return; }
    setSaving(true);
    try { await saveOnboarding(data, token); } catch (e) { console.error(e); }
    setSaving(false);
    onComplete?.(data);
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="ob-root">
        <div className="ob-grid" />
        <div className="ob-glow" />
        <div className="ob-logo"><a href="https://melvo.io" target="_blank" rel="noreferrer" style={{ display: "block", lineHeight: 0 }}><img src="/melvo-logo.png" alt="Melvo" style={{ height: "72px" }} /></a></div>

        {/* Tracker */}
        <div className="ob-tracker">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <div key={n} style={{ display: "flex", alignItems: "center" }}>
                <div className="ob-track-step">
                  <div className={`ob-track-dot${active ? " active" : ""}${done ? " done" : ""}`}>{done ? "✓" : n}</div>
                  <span className={`ob-track-label${active ? " active" : ""}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`ob-track-line${done ? " done" : ""}`} />}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className={`ob-card${step === 4 ? " wide" : ""}`} key={step}>
          {step === 1 && (
            <QuestionnaireStep data={data} onChange={update} onComplete={() => setStep(2)} />
          )}
          {step === 2 && <ConnectStep data={data} onChange={update} />}
          {step === 3 && <PlatformsStep data={data} onChange={update} />}
          {step === 4 && (
            <TemplatePickerStep data={data} onChange={update} onComplete={() => setStep(5)} />
          )}
          {step === 5 && <GoLiveStep data={data} />}

          {/* Nav — hidden on step 1 (self-nav) and step 4 (TemplatePicker owns its button) */}
          {step !== 1 && step !== 4 && (
            <div className="ob-actions">
              {step > 1 && step < 5 && (
                <button className="ob-btn ob-btn-ghost" style={{ flex: "0 0 90px" }} onClick={() => setStep(s => s - 1)}>← Back</button>
              )}
              <button className="ob-btn ob-btn-primary" disabled={!canNext() || saving} onClick={next}>
                {saving ? "Saving..." : step === 5 ? "Go to Dashboard →" : step === 3 ? "Looks good →" : "Continue →"}
              </button>
            </div>
          )}

          {step === 2 && (
            <p className="ob-skip" onClick={() => { update({ integration: "csv" }); setStep(3); }}>
              Skip for now — I'll connect later
            </p>
          )}
        </div>

        {step < 5 && (
          <p style={{ marginTop: 20, fontSize: 12, color: T.muted, position: "relative", zIndex: 1 }}>
            Questions? <a href="mailto:hello@melvo.io" style={{ color: T.accent, textDecoration: "none" }}>hello@melvo.io</a>
          </p>
        )}
      </div>
    </>
  );
}
